import { debugLog, isDebugOn } from "./debug";

export const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/+$/, "") || "";
if (!API_BASE) {
  throw new Error("VITE_API_BASE_URL is required");
}

export function apiUrl(path: string) {
  return API_BASE + "/" + path.replace(/^\/+/, "");
}

interface ApiFetchOptions {
  attempts?: number;
  timeoutMs?: number;
}

export async function apiFetch(
  path: string,
  init?: RequestInit,
  { attempts = 3, timeoutMs = 15000 }: ApiFetchOptions = {},
) {
  const url = apiUrl(path);
  const method = init?.method ?? "GET";
  const headersObj = new Headers(init?.headers);
  if (isDebugOn) headersObj.set("x-debug", "1");
  const requestHeaders = Object.fromEntries(headersObj.entries());

  for (let attempt = 0; attempt < attempts; attempt++) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const started = Date.now();
      const res = await fetch(url, { ...init, headers: headersObj, signal: controller.signal });
      clearTimeout(timeout);

      let bodyPreview = "";
      try {
        bodyPreview = await res.clone().text();
      } catch {
        // ignore
      }

      debugLog({
        url,
        method,
        status: res.status,
        requestHeaders,
        responseHeaders: Object.fromEntries(res.headers.entries()),
        bodyPreview: bodyPreview.slice(0, 100),
        attempt: attempt + 1,
        durationMs: Date.now() - started,
      });

      if ((res.status === 502 || res.status === 503) && attempt < attempts - 1) {
        await new Promise((r) => setTimeout(r, 300 * Math.pow(3, attempt)));
        continue;
      }

      return res;
    } catch (err) {
      clearTimeout(timeout);

      debugLog({
        url,
        method,
        requestHeaders,
        error: err instanceof Error ? err.message : String(err),
        attempt: attempt + 1,
      });

      const isNetworkError =
        err instanceof TypeError || (err instanceof DOMException && err.name === "AbortError");

      if (attempt < attempts - 1 && isNetworkError) {
        await new Promise((r) => setTimeout(r, 300 * Math.pow(3, attempt)));
        continue;
      }

      throw err;
    }
  }

  // Should not reach here but satisfies TypeScript.
  throw new Error("apiFetch failed after all attempts");
}
